import React from 'react';
import * as Yup from 'yup';
import { FileInfo, IFormValue, IProps, IState } from './types';
import { Formik, Form, Field, ErrorMessage, FormikProps, FieldArray, getIn } from 'formik';
import { DatePicker, AutoComplete } from 'formik-antd';
import { RouterSettings } from 'constants/models/routerSettings';
import InsTypeDefinesEN from 'constants/models/insTypeDefinesEN';
import { InsType } from 'constants/enums/insType.enum';
import { InsTypeNameEN } from 'constants/enums/insTypeNameEN.enum';
import InputWithRegex from 'components/InputWithRegex';
import { NumAndEnglishRegex, mobileRegex, emailRegex } from 'utils/commonValidations';
import { EmailDomain, SysOption } from 'bff/models/info';
import service from 'bff/services/memberClaimService';
import _ from 'lodash';
import { storageKeys } from 'constants/enums/storageKeys.enum';

import FormikFocusError from 'components/FormikFocusError';
import sweetAlertService from 'utils/sweetAlertService';
import { SweetAlertIcon } from 'constants/enums/sweetAlertIcon.enum';
// import moment from 'moment';
import moment from 'moment-timezone';
import storageService from 'utils/storageService';
import memberProfileService from 'bff/services/memberProfileService';
import { ClaimAddReq, ClaimPolicyNoContent, MemberProfile } from 'bff/models/member';
import commonService from 'utils/commonService';
import { CodeId } from 'constants/enums/codeId.enum';
import { ClaimCarOwnerType } from 'constants/enums/claimCarOwnerType';
import { ReturnCode } from 'constants/enums/returnCode.enum';

let formInitialValues: IFormValue = {
  INS_TYPE: '',
  CAR_OWNER_TYPE: '',
  LICENSE_PLATE_NUMBER_FRONT: '',
  LICENSE_PLATE_NUMBER_BACK: '',
  IMEI_CODE: '',
  ACCIDENT_TIME: '', // 2020-06-01 14:50:25.521
  ACCIDENT_CAUSE: '',
  INSURED_NAME: '',
  INSURED_ID: '',
  INSURED_ID_ERRMSG: '',
  MOBILE: '',
  EMAIL: '',
  POLICY_NUMBER: '',
  FILES: new Array(0)
};

const formValidationSchema = Yup.object().shape({
  INS_TYPE: Yup.string().required('此為必填欄位'),
  CAR_OWNER_TYPE: Yup.string().when('INS_TYPE', (insType: string, schema: any) => {
    return insType && (insType === InsTypeNameEN.Car || insType === InsTypeNameEN.Moto) ? schema.required('此為必填欄位') : schema;
  }),
  LICENSE_PLATE_NUMBER_FRONT: Yup.string().when('INS_TYPE', (insType: string, schema: any) => {
    return insType && (insType === InsTypeNameEN.Car || insType === InsTypeNameEN.Moto)
      ? schema.required('此為必填欄位').max(4, '最大長度為 4 位元')
      : schema;
  }),
  LICENSE_PLATE_NUMBER_BACK: Yup.string().when('INS_TYPE', (insType: string, schema: any) => {
    return insType && (insType === InsTypeNameEN.Car || insType === InsTypeNameEN.Moto)
      ? schema.required('此為必填欄位').max(4, '最大長度為 4 位元')
      : schema;
  }),
  IMEI_CODE: Yup.string().when('INS_TYPE', (insType: string, schema: any) => {
    return insType && insType === InsTypeNameEN.MobileDevice ? schema.required('此為必填欄位').length(15, 'IMEI 長度為 15 字元') : schema;
  }),
  ACCIDENT_TIME: Yup.string().required('此為必填欄位').nullable(),
  ACCIDENT_CAUSE: Yup.string().required('此為必填欄位'),
  INSURED_NAME: Yup.string().required('此為必填欄位').max(200, '姓名長度不可超過 200 字元').test('contain num', '姓名格式不符', (val: string) => {
    if (val) {
      return !(/[\d]+/.test(val));
    }
    return true;
  }),
  INSURED_ID: Yup.string().required('此為必填欄位').length(10, '長度為 10 字元'),
  MOBILE: Yup.string().required('此為必填欄位').length(10, '手機號碼長度為 10 字元').matches(mobileRegex, '手機格式不符'),
  EMAIL: Yup.string().required('此為必填欄位').matches(emailRegex, '電子信箱格式不符').email('電子信箱格式不符').max(50, '電子信箱長度不可超過 50 字元'),
  POLICY_NUMBER: Yup.string().required('此為必填欄位')
});

const initState: IState = {
  insTypeEn: '',
  emailDomains: new Array(0),
  insCats: Array(0),
  claimAttSysOpts: Array(0),
  claimPolicyNos: Array(0),
  memberProfile: {
    BIRTHDAY: '',
    EMAIL_ADDR: '',
    GENDER: '',
    HEARING_DISABILITY: '',
    MEMBER_ID: '',
    MOBILE: '',
    MEMBERSHIP: new Array(0),
    NAME: '',
    NATIONALITY_ID: '',
    NATIONALITY_TYPE: '',
    SECQUE_ID: '',
    SECQUE_QUESTION: '',
    SECQUE_ANSWER: ''
  },
  compensationCodes: Array(0),
  docCompensationLinks: Array(0),
  imeiErrMsg: '',
  UICodes: new Array(0),
  hideID: true
};

class ClaimService extends React.Component<IProps, IState> {
  private formikRef = React.createRef<FormikProps<IFormValue>>();

  state: IState = initState;

  componentDidMount = async () => {
    storageService.removeItem(storageKeys.ClaimAccidentDate);
    const memberId = storageService.getItem(storageKeys.MemberId);
    if (!memberId) {
      this.props.history.replace(RouterSettings.Home);
      return;
    }

    const memberProfile = await memberProfileService.fetchMemberProfile(memberId) as MemberProfile;

    if (memberProfile) {
      formInitialValues = {
        ...formInitialValues,
        INSURED_ID: memberProfile.MEMBER_ID,
        INSURED_NAME: memberProfile.NAME,
        INS_TYPE: this.props.route?.insType
      };
    }

    const initSetting = await service.fetchInitSetting();
    if (!initSetting) return;

    formInitialValues = {
      ...formInitialValues,
      FILES: this.handleGetFileInfos(initSetting.claimAttSysOpts)
    };

    this.setState({
      insTypeEn: this.props.route?.insType || '',
      insCats: [...initSetting.inscats],
      emailDomains: [...initSetting.emailDomains],
      claimAttSysOpts: [...initSetting.claimAttSysOpts],
      compensationCodes: [...initSetting.compensationCodes],
      docCompensationLinks: [...initSetting.docCompensationLinks],
      memberProfile: memberProfile,
      UICodes: [...initSetting.UICodes]
    });
  }

  componentDidUpdate () {
    /** 抓取電子郵件欄位，限制輸入長度用 */
    const emailInput = document.getElementById('email-input') as HTMLInputElement;
    if (emailInput) {
      emailInput.setAttribute('maxlength', '50');
      emailInput.setAttribute('autocomplete', 'off');
    }
  }

  /**
   * @description 取得上傳區塊資訊
  */
  handleGetFileInfos = (sysOptions: SysOption[]) => {
    const res = new Array(0);
    if (sysOptions && sysOptions.length > 0) {
      _.forEach(sysOptions, (sysOption: SysOption) => {
        res.push({
          FILE_TEXT: sysOption.PARAM_TEXT,
          FILE_NAME: sysOption.PARAM_VALUE,
          FILE_FIELD_NAME: '',
          FILE_BASE64: ''
        });
      });
    }
    return res;
  };

  handleViewToggle = () => {
    this.setState({ hideID: !this.state.hideID });
  };

  render () {
    /**
   * @description 處理「險種」Field 切換後執行的事件
   */
    const handleInsTypeFieldChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
      this.setState(prevState => {
        return {
          ...prevState,
          insTypeEn: event.target.value,
          claimPolicyNos: new Array(0)
        };
      });

      // 根據「險種」導頁
      const path = getPageRoutePath(event.target.value as InsTypeNameEN);

      window.history.pushState({}, '0', path);
      // path && history.push(path);
    };

    /**
     * @description 依據險種取得頁面路由 Path
     */
    const getPageRoutePath = (insTypeVal: InsTypeNameEN) => {
      switch (insTypeVal) {
        case InsTypeNameEN.Travel:
          return RouterSettings.MemberCenter.ClaimServiceTravel;
        case InsTypeNameEN.Car:
          return RouterSettings.MemberCenter.ClaimServiceCar;
        case InsTypeNameEN.Moto:
          return RouterSettings.MemberCenter.ClaimServiceMoto;
        case InsTypeNameEN.Fire:
          return RouterSettings.MemberCenter.ClaimServiceFire;
        case InsTypeNameEN.MobileDevice:
          return RouterSettings.MemberCenter.ClaimServiceMobileDevice;
      }
    };

    /**
     * @description 檢核車牌號碼
     */
    const handleLicensePlate = (insTypeEn: string) => {
      const frontEle = document.querySelector("input[data-fieldname='LICENSE_PLATE_NUMBER_FRONT']") as HTMLInputElement;
      const backEle = document.querySelector("input[data-fieldname='LICENSE_PLATE_NUMBER_BACK']") as HTMLInputElement;
      if (!frontEle || !backEle) return;

      const plateNum = `${frontEle.value}-${backEle.value}`;

      let regex = new RegExp('(^[0-9a-zA-Z]{3}-[0-9a-zA-Z]{4}$|^[0-9a-zA-Z]{3}-[0-9a-zA-Z]{3}$|^[0-9a-zA-Z]{2}-[0-9a-zA-Z]{3}$|^[0-9a-zA-Z]{2}-[0-9a-zA-Z]{2}$)');
      if (insTypeEn === InsTypeNameEN.Car) {
        regex = new RegExp('([0-9a-zA-Z]{4}-[0-9a-zA-Z]{2}$|^[0-9a-zA-Z]{3}-[0-9a-zA-Z]{4}$|^[0-9a-zA-Z]{2}-[0-9a-zA-Z]{4}$)');
      }

      return regex.test(plateNum)
        ? ''
        : '輸入的車牌有誤';
    };

    /**
   * @description 取得電子信箱常用 Domain Name
   */
    const handleGetDomainSource = (val: string) => {
      const emailDNs: any = new Array(0);
      // 有輸入值，且尚未輸入 @ 呈現常用 Domain Name 選單
      if (val && val.indexOf('@') === -1) {
        _.map(this.state.emailDomains, (emailDomain: EmailDomain) => {
          emailDNs.push({ value: `${val}@${emailDomain.EMAIL_DOMAIN_NAME}` });
        });
      }
      return emailDNs;
    };

    /**
     * @description 被保人 email onBlur 事件
    */
    const handleInsuredEmailBlur = () => {
      const emailInput = document.getElementById('email-input') as HTMLInputElement;
      if (!emailInput || !this.formikRef || !this.formikRef.current) return;

      this.formikRef.current.setFieldValue('EMAIL', emailInput.value);
    };

    /**
     * @description 檢核 IMEI 碼
    */
    const handleValidateImeiCode = async (imeiCode: string, setFieldError: any) => {
      const errMsg = await service.checkimei(imeiCode);

      this.setState(prevState => {
        return {
          ...prevState,
          imeiErrMsg: errMsg
        };
      });

      setFieldError('IMEI_CODE', errMsg);

      return errMsg;
    };

    /**
     * @description 取得保單號碼
     */
    const handleGetPolicyNo = _.debounce(async (
      insTypeVal: string, accidentTime: string, accidentPartyId: string, setFieldValue: any,
      imeiCode?: string, plateNumFront?: string, plateNumBack?: string
    ) => {
      setFieldValue('POLICY_NUMBER', '');
      // 未填寫險種、事故日期及時間、被保險人身份證字號
      if (!insTypeVal || !accidentTime || !accidentPartyId) {
        this.setState(prevState => {
          return {
            ...prevState,
            claimPolicyNos: new Array(0)
          };
        });
        return;
      }

      // insType 轉險種代碼
      const insTypeId: any = handleGetInsTypeId(insTypeVal);
      if (!insTypeId) {
        this.setState(prevState => {
          return {
            ...prevState,
            claimPolicyNos: new Array(0)
          };
        });
        return;
      }

      let req: any = {
        INS_TYPE_ID: insTypeId,
        ACCIDENT_DATE: moment(accidentTime).format('YYYY-MM-DD HH:mm:ss.SSS'),
        ACCIDENT_PARTY_ID: accidentPartyId,
        MEMBER_ID: storageService.getItem(storageKeys.MemberId)
      };

      // 汽機車險
      if (insTypeId === InsType.Car || insTypeId === InsType.Moto) {
        // 未填寫車牌號碼
        if (!plateNumFront || !plateNumBack) {
          this.setState(prevState => {
            return {
              ...prevState,
              claimPolicyNos: new Array(0)
            };
          });
          return;
        }

        req = {
          ...req,
          CAR_LICENSE: `${plateNumFront}-${plateNumBack}`
        };
      }

      // 行動裝置險
      if (insTypeId === InsType.MobileDevice) {
        // 未填寫 IMEI 碼
        if (!imeiCode) {
          this.setState(prevState => {
            return {
              ...prevState,
              claimPolicyNos: new Array(0)
            };
          });
          return;
        }

        req = {
          ...req,
          IMEI: imeiCode
        };
      }

      const res = await service.fetchClaimPolicyNo(req);
      if (!res) {
        this.setState(prevState => {
          return {
            ...prevState,
            claimPolicyNos: new Array(0)
          };
        });
        return;
      }

      console.log(res)

      this.setState(prevState => {
        return {
          ...prevState,
          claimPolicyNos: [...res]
        };
      });

      setTimeout(() => {
        setFieldValue('ACCIDENT_TIME', accidentTime);
      });
    }, 500);

    /**
     * @description 取得險種代碼
     */
    const handleGetInsTypeId = (insTypeVal: string) => {
      return Object.keys(InsTypeDefinesEN).find(index => InsTypeDefinesEN[index as InsType] === insTypeVal);
    };

    /**
     * @description 取得上傳圖片區塊
     */
    const getUploadArea = (fileInfos: FileInfo[], errors: any, touched: any) => {
      const acceptExts = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.tiff'];

      return _.map(fileInfos, (fileInfo: FileInfo, index: number) => {
        return (
          <div className="element__form-table-tr" key={index}>
            <div className="element__form-table-td">
              {/* 事故駕駛人駕照 */}
              <div className={'element__form-group' + (getIn(errors, `FILES[${index}].FILE_FIELD_NAME`) && getIn(touched, `FILES[${index}].FILE_FIELD_NAME`) ? ' element__form-group--invalid' : '')}>
                <div className="element__form-group-title">{fileInfo.FILE_TEXT}</div>
                <div className="element__form-group-content">
                  <Field
                    type="text"
                    className="element__input"
                    id={`file_${index}_field`}
                    name={`FILES[${index}].FILE_FIELD_NAME`}
                    readOnly
                  />
                  <label htmlFor={`file_${index}_input`}>
                    <input
                      type="file"
                      id={`file_${index}_input`}
                      accept={acceptExts.join(',')}
                      onChange={(e: any) => {
                        // handleChange(e);
                        handleUpload(e, `FILES[${index}].FILE_FIELD_NAME`, `FILES[${index}].FILE_BASE64`);
                        // setFieldTouched(`FILES[${index}].FILE_FIELD_NAME`, true);
                      }}
                    />
                    <div className="claim-service__upload-btn">上傳</div>
                  </label>
                </div>
              </div>
            </div>
          </div>
        );
      });
    };

    /**
    * @description 上傳購買憑證
    */
    const handleUpload = async (e: any, fieldName: string, fileBaseName: string) => {
      if (!this.formikRef || !this.formikRef.current) return;

      const file = e.target.files[0];

      if (file === undefined) return;

      const acceptExts = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.tiff'];
      const fileExt: string = file.name.substring(file.name.lastIndexOf('.'));
      // 判斷附檔名是否正確
      if (acceptExts.indexOf(fileExt.toLowerCase()) === -1) {
        sweetAlertService.modal({
          icon: SweetAlertIcon.Info,
          html: '檔案格式錯誤'
        });
        return;
      }

      // 判斷檔案大小是否小於 5 MB
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        sweetAlertService.modal({
          icon: SweetAlertIcon.Info,
          html: '上傳的圖片不能超過 5 MB'
        });
        return;
      }

      // 讀取 Base64 資料
      const img = await handleConvertFile(file).then((val: any) => val) as string;

      const setFieldValue = this.formikRef.current.setFieldValue;

      setFieldValue(fieldName, file.name);
      setFieldValue(fileBaseName, img);
    };

    /**
     * @description 讀取檔案
    */
    const handleConvertFile = (file: any) => {
      return new Promise((resolve, reject) => {
        // 建立FileReader物件
        const reader = new FileReader();
        // 註冊onload事件，取得result則resolve (會是一個Base64字串)
        reader.onload = () => { resolve(reader.result); };
        // 註冊onerror事件，若發生error則reject
        reader.onerror = () => { reject(reader.error); };
        // 讀取檔案
        reader.readAsDataURL(file);
      });
    };

    /**
     * @description 取得不可選取的時間
    */
    const handleGetDisabledTime = () => {
      const accidentDate = +storageService.getItem(storageKeys.ClaimAccidentDate);
      if (
        moment(accidentDate).isValid() &&
        moment(moment(accidentDate).format('YYYYMMDD')).isSame(moment(moment().format('YYYYMMDD')))
      ) {
        const nowHour = moment().hours();
        const nowMinute = moment().minute();
        // const select_minute = moment(accidentDate).minute();
        const selectHour = moment(accidentDate).hours();
        return {
          disabledHours: () => range(nowHour + 1, 24),
          disabledMinutes: () => selectHour === nowHour
            ? range(nowMinute + 1, 60)
            : new Array<number>(0)
        };
      }
      return {
        disabledHours: () => new Array<number>(0),
        disabledMinutes: () => new Array<number>(0)
      };
    };

    const range = (start: any, end: any) => {
      const result = [];
      for (let i = start; i < end; i++) {
        result.push(i);
      }
      return result;
    };

    /**
     * @description 回傳選擇的合理時間
     */
    const handleGetVaildateDateTime = (dateTimeVal: moment.Moment) => {
      const date = dateTimeVal.format('YYYYMMDD');

      // 選取非今天日期直接回傳選取日期
      if (!moment(date).isSame(moment(moment().format('YYYYMMDD')))) return dateTimeVal;

      const selectHour = dateTimeVal.hours();
      const nowHour = moment().hours();
      const selectMinute = dateTimeVal.minute();
      const nowMinute = moment().minute();
      if (selectHour < nowHour) return dateTimeVal;

      if ((selectHour > nowHour) || (selectMinute > nowMinute)) {
        return moment(moment(dateTimeVal).format('YYYY/MM/DD'));
      }

      return dateTimeVal;
    };

    /**
     * @description 表單 Submit 事件
     */
    const handleFormSubmit = async (val: IFormValue) => {
      const insTypeId = handleGetInsTypeId(val.INS_TYPE);
      if (!insTypeId) return;

      // 取得新增理賠申請 Req
      const req = handleGetAddReq(insTypeId, val);
      // 新增理賠申請
      const resp = await service.AddClaim(req);
      const res = resp?.TRANRS;
      const resHeader = resp?.MWHEADER;
      if (!res) return;

      if (resHeader?.RETURNCODE === ReturnCode.CodeSuccess) {
        let msg = commonService.getCodeMsg(this.state.UICodes, CodeId.U036);
        if (insTypeId === InsType.Travel || insTypeId === InsType.MobileDevice) {
          msg = commonService.getCodeMsg(this.state.UICodes, CodeId.U035);
        }

        sweetAlertService.modal({
          icon: SweetAlertIcon.Info,
          html: msg
        }).then((isConfirmed) => {
          if (isConfirmed) resetRequiredFields();
        });

        // 下載申請書
        // handleDownload(res.URL);
      }
    };

    /**
     * @description 取得新增理賠申請 Req
     */
    const handleGetAddReq = (insTypeId: string, val: IFormValue) => {
      let claimPolicyNoInfo;

      if (val.POLICY_NUMBER.startsWith('PH') || val.POLICY_NUMBER.startsWith('PB')) {
        claimPolicyNoInfo =
          _.find(this.state.claimPolicyNos, (claim: ClaimPolicyNoContent) => claim.COMPULSORY_NO === val.POLICY_NUMBER);
      } else {
        claimPolicyNoInfo =
          _.find(this.state.claimPolicyNos, (claim: ClaimPolicyNoContent) => claim.POLICY_NO === val.POLICY_NUMBER);
      }

      const req: ClaimAddReq = {
        CLAIM: {
          INS_TYPE_ID: insTypeId,
          APPLY_NO: claimPolicyNoInfo?.APPLY_NO || '',
          POLICY_NO: claimPolicyNoInfo?.POLICY_NO,
          COMPULSORY_NO: claimPolicyNoInfo?.COMPULSORY_NO,
          IS_CAR_OWNER: insTypeId === InsType.Car || insTypeId === InsType.Moto
            ? val.CAR_OWNER_TYPE
            : undefined,
          CAR_LICENSE: insTypeId === InsType.Car || insTypeId === InsType.Moto
            ? `${val.LICENSE_PLATE_NUMBER_FRONT}-${val.LICENSE_PLATE_NUMBER_BACK}`
            : undefined,
          ACCIDENT_DATE: moment(val.ACCIDENT_TIME).format('YYYY-MM-DD HH:mm:ss.SSS'),
          ACCIDENT_NOTE: val.ACCIDENT_CAUSE,
          ACCIDENT_PARTY: val.INSURED_NAME,
          ACCIDENT_PARTY_ID: val.INSURED_ID,
          IMEI_SN: insTypeId === InsType.MobileDevice
            ? val.IMEI_CODE
            : undefined,
          PHONE_NO: val.MOBILE,
          EMAIL: val.EMAIL
        },
        FILES: insTypeId === InsType.Car || insTypeId === InsType.Moto
          ? _.map(val.FILES, (file: FileInfo) => {
            return {
              FILE_NAME: file.FILE_NAME,
              FILE_BASE64: file.FILE_BASE64
            };
          })
          : undefined
      };

      return req;
    };

    /**
     * @description 下載申請書
    */
    const handleDownload = (url: string) => {
      let fileName = '';
      if (url.indexOf('/') !== -1) {
        const lastIndex = url.lastIndexOf('/');
        fileName = url.substring(lastIndex + 1);
      }

      try {
        commonService.downloadFile(url, fileName);
      } catch {
        sweetAlertService.modal({
          icon: SweetAlertIcon.Info,
          title: '下載失敗',
          html: '無法取得相關資訊'
        });
      }
    };

    /**
     * @description 判斷是否可以修改被保人區塊
    */
    const handleDisableInsuredInfo = (insTypeVal: string, carOwnerType: string) => {
      return (insTypeVal !== InsTypeNameEN.Car && insTypeVal !== InsTypeNameEN.Moto) ||
        !carOwnerType ||
        carOwnerType === ClaimCarOwnerType.Self;
    };

    /**
     * @description 取得被保人欄位名稱
     */
    const handleGetInsuredNameText = (insTypeVal: string, carOwnerType: string) => {
      if (handleDisableInsuredInfo(insTypeVal, carOwnerType)) return '被保險人姓名';

      return '駕駛人姓名';
    };

    /**
   * @description 取得被保人 ID 欄位名稱
   */
    const handleGetInsuredIDText = (insTypeVal: string, carOwnerType: string) => {
      if (handleDisableInsuredInfo(insTypeVal, carOwnerType)) return '被保險人身份證字號';

      return '駕駛人身份證字號';
    };

    /**
   * @description 檢核帳號是否已存在格式是否正確
   */
    const handleValidID = _.debounce(async (val: string, values: IFormValue, setFieldError: any, setFieldValue: any, setFieldTouched: any) => {
      const errorVal = await memberProfileService.validMemberId(val).then((res: any) => res.ID);

      setFieldError('INSURED_ID', errorVal);
      setFieldValue('INSURED_ID_ERRMSG', errorVal);
      setFieldTouched('INSURED_ID', true);
      setFieldValue('POLICY_NUMBER', '');
      this.setState(prevState => {
        return {
          ...prevState,
          claimPolicyNos: new Array(0)
        };
      });

      if (errorVal) return;

      setFieldTouched('INSURED_ID', false);
      await handleGetPolicyNo(values.INS_TYPE, values.ACCIDENT_TIME, val, setFieldValue,
        values.IMEI_CODE, values.LICENSE_PLATE_NUMBER_FRONT, values.LICENSE_PLATE_NUMBER_BACK);
    }, 400);

    /**
     * @description 上傳範例下載
     */
    const handleOpenDemoImg = () => {
      if (!this.state.docCompensationLinks || this.state.docCompensationLinks.length === 0) return;

      window.open(this.state.docCompensationLinks[0].URL);
    };

    /**
     * @description 以訊息代號生成 <li> 元素
     * @param {CodeId[]} codeIds 訊息代號陣列
     * @param {string} key 鍵值前綴
     */
    const renderLi = (codeIds: CodeId[], key: string) => {
      if (codeIds.length === 0) return [];
      return codeIds.map((codeId, index) => (
        <li key={`${key}-${index + 1}`}>{commonService.htmlParser(commonService.getCodeMsg(this.state.compensationCodes, codeId))}</li>
      ));
    };

    /**
     * @description 根據不同險種取得不同的備註資訊
     */
    const handleGetNoteInfo = (insTypeEn: string) => {
      let child;
      switch (insTypeEn) {
        case InsTypeDefinesEN[InsType.Travel]:
          child = (
            <>
              {renderLi([CodeId.NOTE_I01_01, CodeId.NOTE_I01_02, CodeId.NOTE_I01_03], 'travel-note')}
            </>
          );
          break;
        case InsTypeDefinesEN[InsType.Car]:
          child = (
            <>
              {renderLi([CodeId.NOTE_I02_01, CodeId.NOTE_I02_02, CodeId.NOTE_I02_03, CodeId.NOTE_I02_04], 'car-note')}
            </>
          );
          break;
        case InsTypeDefinesEN[InsType.Moto]:
          child = (
            <>
              {renderLi([CodeId.NOTE_I03_01, CodeId.NOTE_I03_02, CodeId.NOTE_I03_03, CodeId.NOTE_I03_04], 'moto-note')}
            </>
          );
          break;
        case InsTypeDefinesEN[InsType.Fire]:
          child = (
            <>
              {renderLi([CodeId.NOTE_I04_01, CodeId.NOTE_I04_02, CodeId.NOTE_I04_03], 'fire-note')}
            </>
          );
          break;
        case InsTypeDefinesEN[InsType.MobileDevice]:
          child = (
            <>
              {renderLi([CodeId.NOTE_I05_01, CodeId.NOTE_I05_02, CodeId.NOTE_I05_03], 'mobile-note')}
            </>
          );
          break;
        default:
          child = null;
          break;
      }

      return child;
    };

    /**
     * @description 重置必填欄位
     */
    const resetRequiredFields = () => {
      if (!this.formikRef.current) return;
      const setFieldValue = this.formikRef.current.setFieldValue;
      const setTouched = this.formikRef.current.setTouched;
      setFieldValue('INSURED_ID_ERRMSG', '');
      setFieldValue('CAR_OWNER_TYPE', '');
      setFieldValue('LICENSE_PLATE_NUMBER_FRONT', '');
      setFieldValue('LICENSE_PLATE_NUMBER_BACK', '');
      setFieldValue('IMEI_CODE', '');
      setFieldValue('ACCIDENT_TIME', '');
      setFieldValue('ACCIDENT_CAUSE', '');
      setFieldValue('MOBILE', '');
      setFieldValue('EMAIL', '');
      setFieldValue('POLICY_NUMBER', '');
      ['0', '1', '2'].map(n => {
        setFieldValue(`FILES[${n}].FILE_FIELD_NAME`, '');
        setFieldValue(`FILES[${n}].FILE_BASE64`, '');
      });
      setTouched({}, false);
    };

    return (
      <div className="claim-service">
        <div className="template-container">
          <div className="template-content">
            <div className="claim-service__title">理賠服務</div>
            <div className="claim-service__desc">
              {this.state.insTypeEn === InsTypeDefinesEN[InsType.Travel] && (
                <ol>
                  {renderLi([CodeId.I01_01, CodeId.I01_02], 'travel-claim')}
                </ol>
              )}
              {this.state.insTypeEn === InsTypeDefinesEN[InsType.Car] && (
                <ol>
                  {renderLi([CodeId.I02_01, CodeId.I02_02], 'car-claim')}
                </ol>
              )}
              {this.state.insTypeEn === InsTypeDefinesEN[InsType.Moto] && (
                <ol>
                  {renderLi([CodeId.I03_01, CodeId.I03_02], 'moto-claim')}
                </ol>
              )}
              {this.state.insTypeEn === InsTypeDefinesEN[InsType.Fire] && (
                <ol>
                  {renderLi([CodeId.I04_01, CodeId.I04_02], 'fire-claim')}
                </ol>
              )}
              {
                this.state.insTypeEn === InsTypeDefinesEN[InsType.MobileDevice] && (
                  <ol>
                    {renderLi([CodeId.I05_01, CodeId.I05_02], 'mobile-claim')}
                  </ol>
                )
              }
            </div>
            <div className="claim-service__content">
              <Formik
                innerRef={this.formikRef as any}
                validationSchema={formValidationSchema}
                initialValues={formInitialValues}
                onSubmit={handleFormSubmit}
                enableReinitialize
              >
                {({ values, errors, touched, setFieldTouched, setFieldValue, setTouched, setFieldError, handleBlur, handleChange }) => (
                  <Form>
                    <div className="claim-service__form">
                      <div className="element__form-table">
                        <div className="element__form-table-tr">
                          <div className="element__form-table-td">
                            {/* 險種 */}
                            <div className="element__form-group">
                              <div className="element__form-group-title">險種</div>
                              <div className="element__form-group-content outline-none" data-fieldname="INS_TYPE" tabIndex={1}>
                                {
                                  this.state.insCats && this.state.insCats.length > 0 &&
                                    this.state.insCats
                                      .filter(inscat => inscat.INS_TYPE_ID !== 'I05' && inscat.INS_TYPE_ID !== 'I06' && inscat.INS_TYPE_ID !== 'I07')
                                      .map((inscat, index) => {
                                        return (
                                          <div className="element__radio-group" key={index}>
                                            <label>
                                              <div className="element__radio">
                                                <Field
                                                  type="radio"
                                                  name="INS_TYPE"
                                                  value={InsTypeDefinesEN[inscat.INS_TYPE_ID as InsType]}
                                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                    handleChange('INS_TYPE')(e);
                                                    handleInsTypeFieldChanged(e);
                                                    setFieldValue('INSURED_ID', this.state.memberProfile.MEMBER_ID);
                                                    setFieldValue('INSURED_NAME', this.state.memberProfile.NAME);
                                                    resetRequiredFields();
                                                    this.setState(prevState => {
                                                      return {
                                                        ...prevState,
                                                        imeiErrMsg: ''
                                                      };
                                                    });
                                                    if (e.currentTarget.value === InsTypeNameEN.Travel || e.currentTarget.value === InsTypeNameEN.Fire) {
                                                      handleGetPolicyNo(e.currentTarget.value, values.ACCIDENT_TIME, values.INSURED_ID, setFieldValue,
                                                        values.IMEI_CODE, values.LICENSE_PLATE_NUMBER_FRONT, values.LICENSE_PLATE_NUMBER_BACK);
                                                    }
                                                  }}
                                                />
                                                <div className="element__radio-btn" />
                                              </div>
                                              <span>{inscat.INS_TYPE_NAME}</span>
                                            </label>
                                          </div>
                                        );
                                      })
                                }
                              </div>
                              <div className="element__error-message">
                                <ErrorMessage name="INS_TYPE" />
                              </div>
                            </div>
                          </div>
                        </div>
                        {(values.INS_TYPE === InsTypeDefinesEN[InsType.Car] || values.INS_TYPE === InsTypeDefinesEN[InsType.Moto]) && (
                          <div className="element__form-table-tr">
                            <div className="element__form-table-td">
                              {/* 身份別 */}
                              <div className={'element__form-group' + (errors.CAR_OWNER_TYPE && touched.CAR_OWNER_TYPE ? ' element__form-group--invalid' : '')}>
                                <div className="element__form-group-title">駕駛人身分別*</div>
                                <Field
                                  as="select"
                                  name="CAR_OWNER_TYPE"
                                  data-fieldname="CAR_OWNER_TYPE"
                                  tabIndex={2}
                                  className="element__select"
                                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                    if (e.currentTarget.value === ClaimCarOwnerType.Self) {
                                      setFieldValue('INSURED_ID', this.state.memberProfile.MEMBER_ID);
                                      setFieldValue('INSURED_NAME', this.state.memberProfile.NAME);
                                      setTouched({}, false);
                                      handleGetPolicyNo(values.INS_TYPE, values.ACCIDENT_TIME, this.state.memberProfile.MEMBER_ID, setFieldValue,
                                        values.IMEI_CODE, values.LICENSE_PLATE_NUMBER_FRONT, values.LICENSE_PLATE_NUMBER_BACK);
                                    }
                                    if (e.currentTarget.value === ClaimCarOwnerType.Other) {
                                      if (values.INSURED_ID === this.state.memberProfile.MEMBER_ID) {
                                        setFieldValue('INSURED_ID', '');
                                        setFieldValue('INSURED_NAME', '');
                                        setTouched({}, false);
                                        this.setState(prevState => {
                                          return {
                                            ...prevState,
                                            claimPolicyNos: new Array(0)
                                          };
                                        });
                                      } else {
                                        handleGetPolicyNo(values.INS_TYPE, values.ACCIDENT_TIME, values.INSURED_ID, setFieldValue,
                                          values.IMEI_CODE, values.LICENSE_PLATE_NUMBER_FRONT, values.LICENSE_PLATE_NUMBER_BACK);
                                      }
                                    }
                                    handleChange(e);
                                  }}
                                >
                                  <option value="">請選擇</option>
                                  <option value={ClaimCarOwnerType.Self}>車主本人</option>
                                  <option value={ClaimCarOwnerType.Other}>非車主本人</option>
                                </Field>
                                <div className="element__error-message">
                                  <ErrorMessage name="CAR_OWNER_TYPE" />
                                </div>
                              </div>
                            </div>
                            <div className="element__form-table-td">
                              {/* 車牌號碼 */}
                              <div
                                id="license-plate-number"
                                className={'element__form-group' + ((errors.LICENSE_PLATE_NUMBER_FRONT && touched.LICENSE_PLATE_NUMBER_FRONT) || (errors.LICENSE_PLATE_NUMBER_BACK && touched.LICENSE_PLATE_NUMBER_BACK) ? ' element__form-group--invalid' : '')}
                              >
                                <div className="element__form-group-title">車牌號碼*</div>
                                <div className="element__form-group-content">
                                  <Field
                                    name="LICENSE_PLATE_NUMBER_FRONT"
                                    data-fieldname="LICENSE_PLATE_NUMBER_FRONT"
                                    className="element__input element__input-limit"
                                    autoComplete="off"
                                    maxLength={4}
                                    regex={NumAndEnglishRegex}
                                    component={InputWithRegex}
                                    tabIndex={3}
                                    validate={(val: any) => {
                                      if (val && values.LICENSE_PLATE_NUMBER_BACK) {
                                        return handleLicensePlate(values.INS_TYPE);
                                      }
                                      return '';
                                    }}
                                    onBlur={(e: React.ChangeEvent<HTMLInputElement>) => {
                                      setFieldTouched('LICENSE_PLATE_NUMBER_FRONT', true);
                                      handleBlur(e);
                                    }}
                                    onChange={(val: any) => {
                                      handleGetPolicyNo(values.INS_TYPE, values.ACCIDENT_TIME, values.INSURED_ID, setFieldValue,
                                        values.IMEI_CODE, val, values.LICENSE_PLATE_NUMBER_BACK);
                                    }}
                                  />
                                  <hr />
                                  <Field
                                    name="LICENSE_PLATE_NUMBER_BACK"
                                    data-fieldname="LICENSE_PLATE_NUMBER_BACK"
                                    className="element__input element__input-limit"
                                    autoComplete="off"
                                    maxLength={4}
                                    regex={NumAndEnglishRegex}
                                    component={InputWithRegex}
                                    tabIndex={4}
                                    validate={(val: any) => {
                                      if (val && values.LICENSE_PLATE_NUMBER_FRONT) {
                                        return handleLicensePlate(values.INS_TYPE);
                                      }
                                      return '';
                                    }}
                                    onBlur={(e: React.ChangeEvent<HTMLInputElement>) => {
                                      setFieldTouched('LICENSE_PLATE_NUMBER_BACK', true);
                                      handleBlur(e);
                                    }}
                                    onChange={(val: any) => {
                                      handleGetPolicyNo(values.INS_TYPE, values.ACCIDENT_TIME, values.INSURED_ID, setFieldValue,
                                        values.IMEI_CODE, values.LICENSE_PLATE_NUMBER_FRONT, val);
                                    }}
                                  />
                                </div>
                                <div className="element__error-message">
                                  {
                                    errors.LICENSE_PLATE_NUMBER_FRONT && touched.LICENSE_PLATE_NUMBER_FRONT
                                      ? <ErrorMessage name="LICENSE_PLATE_NUMBER_FRONT" />
                                      : errors.LICENSE_PLATE_NUMBER_BACK && touched.LICENSE_PLATE_NUMBER_BACK
                                        ? <ErrorMessage name="LICENSE_PLATE_NUMBER_BACK" />
                                        : ''
                                  }
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        {values.INS_TYPE === InsTypeDefinesEN[InsType.MobileDevice] && (
                          <div className="element__form-table-tr">
                            <div className="element__form-table-td">
                              {/* IMEI 碼 */}
                              <div className={'element__form-group' + (errors.IMEI_CODE && touched.IMEI_CODE ? ' element__form-group--invalid' : '')}>
                                <div className="element__form-group-title">IMEI 碼*</div>
                                <Field
                                  component={InputWithRegex}
                                  maxLength={15}
                                  autoComplete="off"
                                  name="IMEI_CODE"
                                  className="element__input"
                                  data-fieldname="IMEI_CODE"
                                  tabIndex={5}
                                  validate={() => this.state.imeiErrMsg}
                                  onBlur={() => {
                                    setFieldTouched('IMEI_CODE', true);
                                  }}
                                  onChange={(val: any) => {
                                    if (val && val.length === 15) {
                                      setFieldTouched('IMEI_CODE', true);
                                      const res = handleValidateImeiCode(val, setFieldError);
                                      if (!res) {
                                        handleGetPolicyNo(values.INS_TYPE, values.ACCIDENT_TIME, values.INSURED_ID, setFieldValue,
                                          val, values.LICENSE_PLATE_NUMBER_FRONT, values.LICENSE_PLATE_NUMBER_BACK);
                                      }
                                    }
                                    handleChange('IMEI_CODE')(val);
                                  }}
                                />
                                <div className="element__error-message">
                                  <ErrorMessage name="IMEI_CODE" />
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        <div className="element__form-table-tr">
                          <div className="element__form-table-td">
                            {/* 事故日期及時間 */}
                            <div className={'element__form-group' + (errors.ACCIDENT_TIME && touched.ACCIDENT_TIME ? ' element__form-group--invalid' : '')}>
                              <div className="element__form-group-title">事故日期及時間*</div>
                              <div className="element__form-group-content">
                                <DatePicker
                                  name="ACCIDENT_TIME"
                                  id="accident_time"
                                  className="element__input"
                                  format="YYYY/MM/DD HH:mm"
                                  showTime={{ format: 'HH:mm' }}
                                  showNow={false}
                                  inputReadOnly
                                  data-fieldname="ACCIDENT_TIME"
                                  tabIndex={6}
                                  placeholder="請選擇日期及時間"
                                  disabledDate={(current: any) => {
                                    // 事故發生時間
                                    return current && (current > moment().add(1, 'd') || current < moment('1911/01/01'));
                                  }}
                                  disabledTime={handleGetDisabledTime}
                                  hideDisabledOptions
                                  onBlur={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    handleBlur('ACCIDENT_TIME')(e);
                                    handleGetPolicyNo(values.INS_TYPE, e.currentTarget.value, values.INSURED_ID, setFieldValue,
                                      values.IMEI_CODE, values.LICENSE_PLATE_NUMBER_FRONT, values.LICENSE_PLATE_NUMBER_BACK);
                                  }}
                                  onSelect={(dateTimeVal: any) => {
                                    storageService.setItem(storageKeys.ClaimAccidentDate, JSON.stringify(dateTimeVal.valueOf()));
                                    const vaildateDateTime = handleGetVaildateDateTime(dateTimeVal);
                                    setFieldValue('ACCIDENT_TIME', vaildateDateTime);
                                  }}
                                  onOk={() => {
                                    const element = document.getElementById('accident_time');
                                    if (element) {
                                      element.blur();
                                    }
                                  }}
                                />
                              </div>
                              <div className="element__error-message">
                                <ErrorMessage name="ACCIDENT_TIME" />
                              </div>
                            </div>
                          </div>
                          <div className="element__form-table-td">
                            {/* 事故原因 */}
                            <div className={'element__form-group' + (errors.ACCIDENT_CAUSE && touched.ACCIDENT_CAUSE ? ' element__form-group--invalid' : '')}>
                              <div className="element__form-group-title">事故原因*</div>
                              <Field
                                type="text"
                                maxLength={100}
                                autoComplete="off"
                                name="ACCIDENT_CAUSE"
                                className="element__input"
                                data-fieldname="ACCIDENT_CAUSE"
                                tabIndex={7}
                              />
                              <div className="element__error-message">
                                <ErrorMessage name="ACCIDENT_CAUSE" />
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="element__form-table-tr">
                          <div className="element__form-table-td">
                            {/* 被保險人姓名 */}
                            <div className={'element__form-group' + (errors.INSURED_NAME && touched.INSURED_NAME ? ' element__form-group--invalid' : '')}>
                              <div className="element__form-group-title">{handleGetInsuredNameText(values.INS_TYPE, values.CAR_OWNER_TYPE)}*</div>
                              <Field
                                type="text"
                                name="INSURED_NAME"
                                className="element__input"
                                disabled={handleDisableInsuredInfo(values.INS_TYPE, values.CAR_OWNER_TYPE)}
                                data-fieldname="INSURED_NAME"
                                tabIndex={8}
                                maxLength={200}
                              />
                              <div className="element__error-message">
                                <ErrorMessage name="INSURED_NAME" />
                              </div>
                            </div>
                          </div>
                          <div className="element__form-table-td">
                            {/* 被保險人身分證字號 */}
                            <div className={'element__form-group' + (errors.INSURED_ID && touched.INSURED_ID ? ' element__form-group--invalid' : '')}>
                              <div className="element__form-group-title">{handleGetInsuredIDText(values.INS_TYPE, values.CAR_OWNER_TYPE)}*</div>
                              {this.state.hideID
                                ? <input type="text" className="element__input" value={commonService.hiddenStringLastCharacter(values.INSURED_ID, 5)} disabled readOnly />
                                : (
                                  <Field
                                    type="text"
                                    name="INSURED_ID"
                                    className="element__input"
                                    disabled={handleDisableInsuredInfo(values.INS_TYPE, values.CAR_OWNER_TYPE)}
                                    data-fieldname="INSURED_ID"
                                    tabIndex={9}
                                    maxLength={10}
                                    validate={(val: string) => {
                                      return values.INSURED_ID_ERRMSG;
                                    }}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                      const val = e.currentTarget.value ? e.currentTarget.value.toUpperCase() : e.currentTarget.value;
                                      if (val && val.length === 10) {
                                        handleValidID(val, values, setFieldError, setFieldValue, setFieldTouched);
                                      }
                                      handleChange('INSURED_ID')(e);
                                    }}
                                  />
                                )}
                              <button type="button" className="element__view-btn" onClick={this.handleViewToggle}>
                                {this.state.hideID ? <i className="fas fa-eye-slash" /> : <i className="fas fa-eye" />}
                              </button>
                              <div className="element__error-message">
                                {
                                  errors.INSURED_ID && touched.INSURED_ID
                                    ? errors.INSURED_ID
                                    : ''
                                }
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="element__form-table-tr">
                          <div className="element__form-table-td">
                            {/* 聯絡電話號碼 */}
                            <div className={'element__form-group' + (errors.MOBILE && touched.MOBILE ? ' element__form-group--invalid' : '')}>
                              <div className="element__form-group-title">聯絡電話號碼*</div>
                              <Field
                                component={InputWithRegex}
                                maxLength={10}
                                autoComplete="off"
                                name="MOBILE"
                                className="element__input"
                                data-fieldname="MOBILE"
                                tabIndex={10}
                                onBlur={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  setFieldTouched('MOBILE', true);
                                }}
                              />
                              <div className="element__error-message">
                                <ErrorMessage name="MOBILE" />
                              </div>
                            </div>
                          </div>
                          <div className="element__form-table-td">
                            {/* 聯絡電子信箱 */}
                            <div className={'element__form-group' + (errors.EMAIL && touched.EMAIL ? ' element__form-group--invalid' : '')}>
                              <div className="element__form-group-title">聯絡電子信箱*</div>
                              <AutoComplete
                                name="EMAIL"
                                id="email-input"
                                className="element__antd-input outline-none"
                                data-fieldname="EMAIL"
                                tabIndex={11}
                                options={handleGetDomainSource(values.EMAIL)}
                                onBlur={handleInsuredEmailBlur}
                              />
                              <div className="element__error-message">
                                <ErrorMessage name="EMAIL" />
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="element__form-table-tr">
                          <div className="element__form-table-td">
                            {/* 保單號碼 */}
                            <div className={'element__form-group' + (errors.POLICY_NUMBER && touched.POLICY_NUMBER ? ' element__form-group--invalid' : '')}>
                              <div className="element__form-group-title">
                                {
                                  this.state.insTypeEn !== InsTypeNameEN.Car && this.state.insTypeEn !== InsTypeNameEN.Moto
                                    ? '保單號碼*'
                                    : '保單號碼 / 強制證號*'
                                }
                              </div>
                              <Field
                                as="select"
                                name="POLICY_NUMBER"
                                className="element__select"
                                data-fieldname="POLICY_NUMBER"
                                tabIndex={12}
                              >
                                <option value="">請選擇</option>
                                {
                                  this.state.claimPolicyNos && this.state.claimPolicyNos.length > 0 && _.map(this.state.claimPolicyNos, (claimPolicyNo: ClaimPolicyNoContent, index: number) => (
                                    <option value={claimPolicyNo.POLICY_NO ? claimPolicyNo.POLICY_NO : claimPolicyNo.COMPULSORY_NO} key={index}>
                                      {
                                        claimPolicyNo.COMPULSORY_NO && claimPolicyNo.POLICY_NO
                                          ? `${claimPolicyNo.POLICY_NO}/${claimPolicyNo.COMPULSORY_NO}`
                                          : claimPolicyNo.COMPULSORY_NO
                                            ? `${claimPolicyNo.COMPULSORY_NO}`
                                            : `${claimPolicyNo.POLICY_NO}`
                                      }
                                    </option>
                                  ))
                                }
                              </Field>
                              <div className="element__error-message">
                                <ErrorMessage name="POLICY_NUMBER" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {(values.INS_TYPE === InsTypeDefinesEN[InsType.Car] || values.INS_TYPE === InsTypeDefinesEN[InsType.Moto]) &&
                      this.state.claimAttSysOpts && this.state.claimAttSysOpts.length > 0 &&
                      (
                        <div className="claim-service__upload">
                          <div className="claim-service__upload-title">
                            <span>應備證明文件</span>
                            <span onClick={handleOpenDemoImg}>(上傳範例)</span>
                          </div>
                          <div className="claim-service__upload-container">
                            <div className="element__form-table outline-none" data-fieldname="FILES" tabIndex={13}>
                              <FieldArray name="FILES">
                                {
                                  () => (
                                    <>
                                      {
                                        this.state.claimAttSysOpts && this.state.claimAttSysOpts.length > 0 && getUploadArea(values.FILES, errors, touched)
                                      }
                                    </>
                                  )
                                }
                              </FieldArray>
                              {
                                //   <div className="element__form-table-tr">
                                //   <div className="element__form-table-td">
                                //     {/* 事故駕駛人駕照 */}
                                //     <div className={'element__form-group' + (errors.DRIVE_LICENSE && touched.DRIVE_LICENSE ? ' element__form-group--invalid' : '')}>
                                //       <div className="element__form-group-title">事故駕駛人駕照</div>
                                //       <div className="element__form-group-content">
                                //         <input type="text" className="element__input" readOnly />
                                //         <label>
                                //           <div className="claim-service__upload-btn">上傳</div>
                                //           <Field
                                //             type="file"
                                //             name="DRIVE_LICENSE"
                                //             accept=".jpg,.jpeg,.png,.gif,.bmp,.tiff"
                                //             data-fieldname="DRIVE_LICENSE"
                                //             tabIndex={13}
                                //           />
                                //         </label>
                                //       </div>
                                //       <div className="element__error-message">
                                //         <ErrorMessage name="DRIVE_LICENSE" />
                                //       </div>
                                //     </div>
                                //   </div>
                                // </div>
                                // <div className="element__form-table-tr">
                                //   <div className="element__form-table-td">
                                //     {/* 本車行照 */}
                                //     <div className={'element__form-group' + (errors.VEHICLE_LICENSE && touched.VEHICLE_LICENSE ? ' element__form-group--invalid' : '')}>
                                //       <div className="element__form-group-title">本車行照</div>
                                //       <div className="element__form-group-content">
                                //         <input type="text" className="element__input" readOnly />
                                //         <label>
                                //           <div className="claim-service__upload-btn">上傳</div>
                                //           <Field
                                //             type="file"
                                //             name="VEHICLE_LICENSE"
                                //             accept=".jpg,.jpeg,.png,.gif,.bmp,.tiff"
                                //             data-fieldname="VEHICLE_LICENSE"
                                //             tabIndex={14}
                                //           />
                                //         </label>
                                //       </div>
                                //       <div className="element__error-message">
                                //         <ErrorMessage name="VEHICLE_LICENSE" />
                                //       </div>
                                //     </div>
                                //   </div>
                                // </div>
                                // <div className="element__form-table-tr">
                                //   <div className="element__form-table-td">
                                //     {/* 交通事故登記聯單 */}
                                //     <div className={'element__form-group' + (errors.ACCIDENT_REGIST && touched.ACCIDENT_REGIST ? ' element__form-group--invalid' : '')}>
                                //       <div className="element__form-group-title">交通事故登記聯單</div>
                                //       <div className="element__form-group-content">
                                //         <input type="text" className="element__input" readOnly />
                                //         <label>
                                //           <div className="claim-service__upload-btn">上傳</div>
                                //           <Field
                                //             type="file"
                                //             name="ACCIDENT_REGIST"
                                //             accept=".jpg,.jpeg,.png,.gif,.bmp,.tiff"
                                //             data-fieldname="ACCIDENT_REGIST"
                                //             tabIndex={15}
                                //           />
                                //         </label>
                                //       </div>
                                //       <div className="element__error-message">
                                //         <ErrorMessage name="ACCIDENT_REGIST" />
                                //       </div>
                                //     </div>
                                //   </div>
                                // </div>

                              }
                            </div>
                          </div>
                        </div>
                      )}
                    <div className="claim-service__remark">
                      <div className="claim-service__remark-title">注意事項</div>
                      <ol>
                        {
                          handleGetNoteInfo(values.INS_TYPE)
                        }
                      </ol>
                    </div>
                    <div className="claim-service__controls">
                      <button type="submit" className="element__button element__button--red">確認送出</button>
                      <button type="button" className="element__button element__button--red-border" onClick={() => this.props.history.replace(RouterSettings.Home)}>取消</button>
                    </div>
                    <FormikFocusError />
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default ClaimService;
